// eslint-disable-next-line import/order
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryResponseType, WsResponse } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { DelegationBuyer } from '@1po/1po-bff-fe-spec/generated/delegation/response/model/DelegationBuyer';
import { DelegationSeller } from '@1po/1po-bff-fe-spec/generated/delegation/response/model/DelegationSeller';
import { CommercialLink } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { call, cancel, delay, fork, put, race, select, take, takeEvery } from '@redux-saga/core/effects';
import { eventChannel, SagaIterator, Task } from 'redux-saga';

import { fetchBasketRequest } from 'domains/basket/Basket.store';
import {
  fetchEstimateByIdRequest,
  fetchEstimateHistoryRequest,
  fetchSettingsRequest,
  getCurrentEstimateId,
  resetEstimateHistory,
} from 'domains/estimate/Estimate.store';
import { orderSubscriptionSaga } from 'domains/order/Order.store';
import { resetReferencesPriceData, resetStockData } from 'domains/references/References.store';
import {
  fetchVehicleSearchHistorySubscriptionSaga,
  getUserCommercialLink,
  getUserProfileReconnectSaga,
  getUserProfileSearchStatus,
  reloadSubscriptionBeUserSaga,
  resetDelegationSession,
  setCommercialLink,
  setResubscribeableFieldsUndefined,
  setResubscribeableFieldsUndefinedForAppReconnection,
  setUserProfileSearchStatus,
} from 'domains/user/User.store';
import { wsConnect, wsDisconnect } from 'domains/webSockets/WebSocket.store';
import { notifyTop } from 'UI/Notification/notification';
import { FOUND, LOADING, sagaGuard } from 'utils';

import {
  sendGetDelegationSeller,
  sendGetPossibleBuyersForDelegation,
  sendGetPossibleSellersForDelegation,
  sendConnectDelegation,
  sendDisconnectDelegation,
  sendExtendDelegation,
  sendGetActiveDelegation,
} from './DelegationSession.api';
import {
  clearDelegationRestoreMarker,
  markDelegationRestoreEligible,
  shouldRestoreDelegationSession,
} from './DelegationSession.restore';
import {
  cancelCurrentDelegationSellerSaga,
  getDelegationSellerResponseSaga,
  getDelegationSellerSaga,
  getPossibleBuyersForDelegationResponseSaga,
  getPossibleBuyersForDelegationSaga,
  getPossibleSellersForDelegationResponseSaga,
  getPossibleSellersForDelegationSaga,
  clearError,
  connectDelegationSessionRequest,
  connectDelegationSessionResponse,
  resetDelegationSessionSaga,
  delegationContextRefreshRequest,
  delegationContextReverted,
  delegationSessionErrorAction,
  delegationSessionUpdatedEvent,
  disconnectDelegationSessionRequest,
  disconnectDelegationSessionResponse,
  extendDelegationSessionRequest,
  extendDelegationSessionResponse,
  getActiveDelegationSessionRequest,
  getActiveDelegationSessionResponse,
  getActiveSession,
  getActiveSessionStatus,
  getSessionTimeRemaining,
  setConnected,
  setConnecting,
  setDisconnected,
  setDisconnecting,
  setError,
  setSessionNotFound,
  setLoading,
  setHeartbeatActive,
  setInitialState,
  setPossibleBuyersForSellerToDelegate,
  setPossibleBuyersForSellerToDelegateNoData,
  setPossibleSellersToDelegate,
  setPossibleSellersToDelegateNoData,
  updateSession,
} from './DelegationSession.store';
import { ConnectDelegationRequest, DelegationSessionResponse } from './DelegationSession.types';

const HEARTBEAT_INTERVAL_MS = 300000;
const EXTEND_THRESHOLD_MS = 10 * 60 * 1000;
const DEFAULT_EXTEND_MINUTES = 30;
const TRADING_PROFILE_SYNC_MAX_RETRIES = 25;
const TRADING_PROFILE_SYNC_RETRY_DELAY_MS = 200;
const CONTEXT_REFRESH_RETRY_DELAY_MS = 1000;
const INIT_PROBE_FOLLOWUP_DELAY_MS = 2000;
const DELEGATION_SYNC_EVENT_KEY = '1po.delegation.sync';
const DELEGATION_CONNECT_FAILED_MESSAGE = 'Unable to delegate';
type DelegationSyncReason = 'connected' | 'reset';

type DelegationErrorPayload = {
  message?: string;
  errorType?: string;
  errorDetail?: Array<{
    name?: string;
    value?: string;
  }>;
};

type DelegationErrorSourcePayload = DelegationErrorPayload | string | undefined;
type DelegationConnectErrorPayload = DelegationErrorPayload | string;
// boolean = COMMAND_PROCESSING_RESPONSE(true) ack; WS middleware remaps it to CONNECT_DELEGATION_SESSION_RESPONSE
// before the real COMMAND_LOOKUP_RESPONSE arrives.
type DelegationConnectResponsePayload = DelegationSessionResponse | null | DelegationConnectErrorPayload | boolean;

const isDelegationSessionResponse = (payload: DelegationConnectResponsePayload): payload is DelegationSessionResponse =>
  !!payload && typeof payload === 'object' && 'active' in payload;

/**
 * Sync FE trading commercial link to the delegated buyer link while Connect-as is active.
 * Preserves currency from the previous link when the session payload omits it.
 */
export function* syncDelegatedCommercialLinkSaga(session: DelegationSessionResponse | null | undefined): SagaIterator {
  const delegatedLink = session?.active === true ? session.commercialLink : undefined;
  if (!delegatedLink) {
    return;
  }

  const currentCommercialLink: CommercialLink | null = yield select(getUserCommercialLink);
  const commercialLinkToApply: CommercialLink = {
    ...delegatedLink,
    currencyCode: delegatedLink.currencyCode || currentCommercialLink?.currencyCode || 'EUR',
  };
  yield put(setCommercialLink(commercialLinkToApply));
}

const getValueIfNotBlank = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const getPayloadMessage = (payload: DelegationErrorSourcePayload): string | undefined => {
  if (typeof payload === 'string') {
    return getValueIfNotBlank(payload);
  }

  return getValueIfNotBlank(payload?.message);
};

const getPayloadErrorDetails = (payload: DelegationErrorSourcePayload): string | undefined => {
  if (!payload || typeof payload === 'string' || !Array.isArray(payload.errorDetail)) {
    return undefined;
  }

  const details = payload.errorDetail
    .map((detail) => {
      const detailName = getValueIfNotBlank(detail?.name) || '';
      const detailValue = getValueIfNotBlank(detail?.value) || '';
      return detailName && detailValue ? `${detailName}: ${detailValue}` : detailName || detailValue;
    })
    .filter((detail) => detail.length > 0);

  return details.length > 0 ? details.join(' | ') : undefined;
};

const getPayloadErrorType = (payload: DelegationErrorSourcePayload): string | undefined => {
  if (!payload || typeof payload === 'string') {
    return undefined;
  }

  return getValueIfNotBlank(payload.errorType);
};

const getActionErrorDetailMessage = (errorDetail: unknown): string | undefined => {
  if (typeof errorDetail === 'string') {
    return getValueIfNotBlank(errorDetail);
  }

  if (errorDetail && typeof errorDetail === 'object') {
    return getValueIfNotBlank((errorDetail as { message?: string }).message);
  }

  return undefined;
};

const getDelegationErrorDescription = (action: WsResponse): string | undefined => {
  const payload = action.payload as DelegationErrorSourcePayload;

  return (
    getPayloadMessage(payload) ||
    getPayloadErrorDetails(payload) ||
    getPayloadErrorType(payload) ||
    getActionErrorDetailMessage(action.errorDetail)
  );
};

const hasDelegationContextChanged = (
  currentSession: DelegationSessionResponse | null | undefined,
  updatedSession: DelegationSessionResponse,
): boolean => {
  if (!currentSession?.active) {
    return true;
  }

  return (
    currentSession.delegatedBuyerTresorCode !== updatedSession.delegatedBuyerTresorCode ||
    currentSession.delegatedSellerTresorCode !== updatedSession.delegatedSellerTresorCode
  );
};

function createHeartbeatChannel(intervalMs: number = HEARTBEAT_INTERVAL_MS) {
  return eventChannel((emitter) => {
    const interval = setInterval(() => {
      emitter({ type: 'TICK' });
    }, intervalMs);

    // Return cleanup function
    return () => {
      clearInterval(interval);
    };
  });
}

function createWindowActivationChannel() {
  return eventChannel((emitter) => {
    const emitIfVisible = () => {
      if (document.visibilityState === 'visible') {
        emitter({ type: 'WINDOW_ACTIVE' });
      }
    };

    globalThis.addEventListener('focus', emitIfVisible);
    globalThis.addEventListener('visibilitychange', emitIfVisible);

    return () => {
      globalThis.removeEventListener('focus', emitIfVisible);
      globalThis.removeEventListener('visibilitychange', emitIfVisible);
    };
  });
}

function createCrossTabDelegationSyncChannel() {
  return eventChannel((emitter) => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === DELEGATION_SYNC_EVENT_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue) as { reason?: DelegationSyncReason };
          emitter({ type: 'CROSS_TAB_SYNC', reason: payload.reason ?? 'reset' });
        } catch {
          emitter({ type: 'CROSS_TAB_SYNC', reason: 'reset' });
        }
      }
    };

    globalThis.addEventListener('storage', onStorage);

    return () => {
      globalThis.removeEventListener('storage', onStorage);
    };
  });
}

function broadcastDelegationSyncSignal(reason: DelegationSyncReason) {
  try {
    const payload = JSON.stringify({ ts: Date.now(), reason });
    localStorage.setItem(DELEGATION_SYNC_EVENT_KEY, payload);
    localStorage.removeItem(DELEGATION_SYNC_EVENT_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Unable to broadcast cross-tab delegation sync signal', error);
  }
}

function* performFullDelegationReset(options?: {
  broadcast?: boolean;
  requestUserProfile?: boolean;
  broadcastReason?: DelegationSyncReason;
}): SagaIterator {
  yield call(clearDelegationRestoreMarker);

  if (options?.broadcast) {
    yield call(broadcastDelegationSyncSignal, options.broadcastReason ?? 'reset');
  }

  // Clear state BEFORE reconnecting — onConnect won't re-dispatch getActiveDelegationSessionRequest.
  yield put(setDisconnected());
  yield put(delegationContextReverted());
  yield put(resetStockData());

  if (options?.requestUserProfile) {
    yield put(wsDisconnect());
    yield put(wsConnect());
  }

  yield put(setResubscribeableFieldsUndefinedForAppReconnection());
  yield put(reloadSubscriptionBeUserSaga());
  yield put(resetEstimateHistory());

  yield put(delegationContextRefreshRequest({ profileAlreadyPending: options?.requestUserProfile ?? false }));
}

function* requestDelegationStateSyncSaga(): SagaIterator {
  const currentSession: DelegationSessionResponse | null = yield select(getActiveSession);
  const shouldRestore = yield call(shouldRestoreDelegationSession, currentSession);

  if (!shouldRestore) {
    return;
  }

  yield put(getActiveDelegationSessionRequest());
}

function* runHeartbeatLoop(channel: ReturnType<typeof createHeartbeatChannel>): SagaIterator {
  while (true) {
    const result = yield race({
      tick: take(channel),
      stop: take([disconnectDelegationSessionResponse.type, setDisconnected.type]),
    });

    if (result.stop) {
      // eslint-disable-next-line no-console
      console.info('Heartbeat stopped: disconnect signal received');
      return;
    }

    yield call(heartbeatTickSaga);
  }
}

/** Starts heartbeat: interval ticks every 5 min, extends session when needed, stops on disconnect or expiration. */
export function* startHeartbeatSaga(): SagaIterator {
  try {
    // eslint-disable-next-line no-console
    console.info('Starting heartbeat mechanism');

    yield put(setHeartbeatActive(true));
    const channel = yield call(createHeartbeatChannel);

    try {
      yield call(runHeartbeatLoop, channel);
    } finally {
      channel.close();
      yield put(setHeartbeatActive(false));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Heartbeat saga error:', error);
    yield put(setHeartbeatActive(false));
  }
}

export function* heartbeatTickSaga(): SagaIterator {
  try {
    const session = yield select(getActiveSession);
    const timeRemaining = yield select(getSessionTimeRemaining);

    // Check if session still active
    if (!session?.active) {
      // eslint-disable-next-line no-console
      console.warn('Heartbeat tick: session no longer active');
      return;
    }

    // Check if page is hidden (don't extend if user not viewing)
    if (document.hidden) {
      // eslint-disable-next-line no-console
      console.info('Heartbeat tick: page hidden, skipping');
      return;
    }

    // Extend if < 10 min remaining and not yet expired (timeRemaining===0 means clamped/expired; backend pushes update, skip extend).
    if (timeRemaining !== null && timeRemaining > 0 && timeRemaining < EXTEND_THRESHOLD_MS) {
      // eslint-disable-next-line no-console
      console.info('Heartbeat tick: session expiring soon, extending', { timeRemaining });
      yield put(extendDelegationSessionRequest({ minutes: DEFAULT_EXTEND_MINUTES }));
    } else if (timeRemaining !== null && timeRemaining > 0) {
      // eslint-disable-next-line no-console
      console.info('Heartbeat tick: session healthy', { timeRemaining });
    }
    // timeRemaining === 0 or null: session expired by local clock; await backend event.
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Heartbeat tick error:', error);
  }
}

const isConnectOrRestoreAction = (actionType: string): boolean =>
  actionType === connectDelegationSessionResponse.type || actionType === getActiveDelegationSessionResponse.type;

const shouldStartHeartbeatForAction = (action: WsResponse<DelegationSessionResponse | null>): boolean =>
  isConnectOrRestoreAction(action.type) && !!action.payload?.active;

const shouldStopHeartbeatForAction = (action: WsResponse<DelegationSessionResponse | null>): boolean =>
  action.type === disconnectDelegationSessionResponse.type ||
  action.type === setDisconnected.type ||
  (isConnectOrRestoreAction(action.type) && !action.payload?.active);

export function* watchHeartbeatLifecycleSaga(): SagaIterator {
  let heartbeatTask: Task | null = null;

  while (true) {
    const action: WsResponse<DelegationSessionResponse | null> = yield take([
      connectDelegationSessionResponse.type,
      getActiveDelegationSessionResponse.type,
      disconnectDelegationSessionResponse.type,
      setDisconnected.type,
    ]);

    const shouldStart = shouldStartHeartbeatForAction(action);
    const shouldStop = shouldStopHeartbeatForAction(action);

    if (shouldStart && !heartbeatTask) {
      heartbeatTask = yield fork(startHeartbeatSaga);
    }

    if (shouldStop && heartbeatTask) {
      yield cancel(heartbeatTask);
      heartbeatTask = null;
    }
  }
}

export function* connectDelegationSessionRequestSaga(action: PayloadAction<ConnectDelegationRequest>): SagaIterator {
  yield put(setConnecting());
  yield put(clearError());
  yield put(sendConnectDelegation(action.payload));
}

export function* connectDelegationSessionResponseSaga(
  action: WsResponse<DelegationConnectResponsePayload>,
): SagaIterator {
  const session = action.payload;

  if (typeof session === 'boolean') {
    return;
  }

  if (isDelegationSessionResponse(session) && session.active) {
    yield call(markDelegationRestoreEligible, session.expiresAt);
    yield call(broadcastDelegationSyncSignal, 'connected');
    yield put(setConnected(session));
    yield call(syncDelegatedCommercialLinkSaga, session);
    yield put(wsDisconnect());
    yield put(wsConnect());
    yield take(getActiveDelegationSessionResponse.type);
    // Dispatch delegationContextRefreshRequest regardless of active: CQRS lag may return active:false briefly
    // (INIT probe skips reset); profileAlreadyPending:true since onConnect already sent the profile request.
    yield put(delegationContextRefreshRequest({ profileAlreadyPending: true }));
    return;
  }

  const isSuccessfulResponse =
    action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_SUCCESS ||
    action.queryResponseType === QueryResponseType.COMMAND_LOOKUP_RESPONSE ||
    action.queryResponseType === QueryResponseType.QUERY_SUCCESS_RESPONSE ||
    action.queryResponseType === QueryResponseType.INIT ||
    action.queryResponseType === QueryResponseType.EVENT_UPDATE;
  if (!isSuccessfulResponse) {
    const errorDescription = getDelegationErrorDescription(action);
    yield put(setError(errorDescription || 'Failed to manage delegation session'));
    yield call(notifyTop, 'error', DELEGATION_CONNECT_FAILED_MESSAGE, errorDescription);
  }
}

export function* disconnectDelegationSessionRequestSaga(): SagaIterator {
  yield put(setDisconnecting());
  yield put(sendDisconnectDelegation());
}

export function* disconnectDelegationSessionResponseSaga(_action: WsResponse<boolean>): SagaIterator {
  const disconnectSucceeded =
    _action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_SUCCESS ||
    _action.queryResponseType === QueryResponseType.QUERY_SUCCESS_RESPONSE ||
    _action.payload;

  if (!disconnectSucceeded) {
    // Notify the user but still perform the full reset — if the backend rejects the disconnect
    // (e.g., session already expired), the frontend must clean up regardless of the response.
    yield call(notifyTop, 'error', 'Unable to disconnect delegation session', undefined);
  }

  yield call(performFullDelegationReset, { broadcast: true, requestUserProfile: true, broadcastReason: 'reset' });
}

export function* extendDelegationSessionRequestSaga(action: PayloadAction<{ minutes?: number }>): SagaIterator {
  const minutes = action.payload.minutes || DEFAULT_EXTEND_MINUTES;
  yield put(sendExtendDelegation(minutes));
}

export function* extendDelegationSessionResponseSaga(_action: WsResponse<boolean>): SagaIterator {
  const succeeded =
    _action.queryResponseType === QueryResponseType.COMMAND_RESPONSE_SUCCESS ||
    _action.queryResponseType === QueryResponseType.QUERY_SUCCESS_RESPONSE ||
    _action.payload;

  if (!succeeded) {
    yield put(setError('Failed to extend delegation session'));
    return;
  }

  // Session extended successfully — subscription will push the updated expiresAt.
  // eslint-disable-next-line no-console
  console.info('Delegation session extended successfully');
  yield* [];
}

export function* getActiveDelegationSessionRequestSaga(): SagaIterator {
  yield put(setLoading());
  yield put(sendGetActiveDelegation());
}

/** Watchdog called when an INIT probe returns active:false but the Redux session is still active.
 *  Waits briefly for the expected EVENT_UPDATE to arrive; if the session is still active after
 *  the delay it means the EVENT_UPDATE was missed, so a follow-up non-INIT probe is dispatched. */
export function* scheduleInitProbeFollowupSaga(): SagaIterator {
  yield delay(INIT_PROBE_FOLLOWUP_DELAY_MS);
  const sessionAfterDelay: DelegationSessionResponse | null = yield select(getActiveSession);
  if (sessionAfterDelay?.active === true) {
    yield put(getActiveDelegationSessionRequest());
  }
}

export function* getActiveDelegationSessionResponseSaga(
  action: WsResponse<DelegationSessionResponse | null>,
): SagaIterator {
  const session = action.payload;
  if (session?.active) {
    yield call(markDelegationRestoreEligible, session.expiresAt);
    const currentSession: DelegationSessionResponse | null = yield select(getActiveSession);
    const contextChanged = hasDelegationContextChanged(currentSession, session);
    yield put(setConnected(session));
    yield call(syncDelegatedCommercialLinkSaga, session);
    if (contextChanged) {
      yield put(delegationContextRefreshRequest());
    }
  } else {
    yield call(clearDelegationRestoreMarker);

    const currentSession = yield select(getActiveSession);
    if (currentSession?.active === true) {
      // Guard: INIT probe after reconnect may return active:false due to CQRS lag; skip reset and await EVENT_UPDATE.
      const isInitProbe = action.queryResponseType === QueryResponseType.INIT;
      if (isInitProbe) {
        // INIT guard skipped reset. Schedule a follow-up probe in case EVENT_UPDATE is missed
        // (subscription race at WS reconnect) — keeps the session from getting stuck as stale-active.
        yield call(scheduleInitProbeFollowupSaga);
      } else {
        yield call(performFullDelegationReset, { broadcast: true, requestUserProfile: true, broadcastReason: 'reset' });
      }
    } else {
      const sessionStatusBeforeUpdate = yield select(getActiveSessionStatus);
      yield put(setSessionNotFound());

      if (sessionStatusBeforeUpdate === LOADING) {
        yield put(delegationContextRefreshRequest());
      }
    }
  }
}

export function* resetDelegationSessionRequestSaga(): SagaIterator {
  yield put(resetDelegationSession());
  yield put(setInitialState());
}

export function* getPossibleSellersForDelegationRequestSaga(): SagaIterator {
  yield put(setPossibleSellersToDelegateNoData(LOADING));
  yield put(sendGetPossibleSellersForDelegation());
}

export function* getPossibleSellersForDelegationResponseHandlerSaga(
  action: WsResponse<{ sellers: DelegationSeller[] }>,
): SagaIterator {
  const payload = action.payload;
  if (payload?.sellers) {
    yield put(setPossibleSellersToDelegate({ sellers: payload.sellers }));
  }
}

export function* getPossibleBuyersForDelegationRequestSaga(action: PayloadAction<string>): SagaIterator {
  const sellerTresorCode = action.payload;
  yield put(
    setPossibleBuyersForSellerToDelegateNoData({
      sellerId: sellerTresorCode,
      status: LOADING,
    }),
  );
  yield put(sendGetPossibleBuyersForDelegation({ sellerTresorCode }));
}

export function* getPossibleBuyersForDelegationResponseHandlerSaga(
  action: WsResponse<{ buyers: DelegationBuyer[]; sellerId: string }>,
): SagaIterator {
  const payload = action.payload;
  if (payload?.sellerId) {
    yield put(setPossibleBuyersForSellerToDelegate({ sellerId: payload.sellerId, buyers: payload.buyers || [] }));
  }
}

export function* getDelegationSellerRequestSaga(action: PayloadAction<string>): SagaIterator {
  yield put(setPossibleSellersToDelegateNoData(LOADING));
  yield put(sendGetDelegationSeller({ sellerBirCode: action.payload }));
}

export function* getDelegationSellerResponseHandlerSaga(
  action: WsResponse<{ seller: DelegationSeller | undefined }>,
): SagaIterator {
  const seller = action.payload?.seller;
  if (seller) {
    yield put(setPossibleSellersToDelegate({ sellers: [seller] }));
  }
}

export function* cancelCurrentDelegationSellerRequestSaga(): SagaIterator {
  yield put(setPossibleSellersToDelegateNoData(null));
}

export function* delegationSessionUpdatedEventSaga(action: WsResponse<DelegationSessionResponse>): SagaIterator {
  try {
    const update = action.payload;

    if (!update) {
      // eslint-disable-next-line no-console
      console.warn('Received empty delegation session update');
      return;
    }

    // eslint-disable-next-line no-console
    console.info('Delegation session update received', {
      active: update.active,
      expiresAt: update.expiresAt,
    });

    if (update.active) {
      yield call(markDelegationRestoreEligible, update.expiresAt);
      yield call(syncDelegatedCommercialLinkSaga, update);
    } else {
      yield call(clearDelegationRestoreMarker);
    }

    const currentSession = yield select(getActiveSession);
    const shouldRefreshContext = update.active && hasDelegationContextChanged(currentSession, update);
    const shouldTriggerDisconnectFlow = !update.active && currentSession?.active === true;

    if (shouldTriggerDisconnectFlow) {
      // eslint-disable-next-line no-console
      console.info('Session became inactive, triggering disconnect flow');
      yield call(performFullDelegationReset, { broadcast: true, requestUserProfile: true, broadcastReason: 'reset' });
    } else if (shouldRefreshContext) {
      yield put(updateSession(update));
      yield put(delegationContextRefreshRequest());
    } else {
      yield put(updateSession(update));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error handling session update:', error);
  }
}

export function* delegationSessionErrorSaga(action: WsResponse): SagaIterator {
  const activeSession = yield select(getActiveSession);
  const sessionStatus = yield select(getActiveSessionStatus);

  const errorDescription = getDelegationErrorDescription(action);
  const errorMessage = errorDescription || 'Failed to manage delegation session';
  yield put(setError(errorMessage));

  if (!activeSession?.active && sessionStatus === LOADING) {
    yield call(notifyTop, 'error', DELEGATION_CONNECT_FAILED_MESSAGE, errorDescription);
    yield put(setSessionNotFound());
    yield put(delegationContextRefreshRequest());
  }
}

function* waitForTradingProfileReady(): SagaIterator<boolean> {
  for (let attempt = 0; attempt < TRADING_PROFILE_SYNC_MAX_RETRIES; attempt += 1) {
    const userProfileSearchStatus = yield select(getUserProfileSearchStatus);

    if (userProfileSearchStatus === FOUND) {
      return true;
    }

    yield race({
      profileStatusSet: take(setUserProfileSearchStatus.type),
      timeout: delay(TRADING_PROFILE_SYNC_RETRY_DELAY_MS),
    });
  }

  return false;
}

export function* delegationContextRefreshSaga(
  action?: ReturnType<typeof delegationContextRefreshRequest>,
): SagaIterator {
  const profileAlreadyPending = action?.payload?.profileAlreadyPending ?? false;

  // profileAlreadyPending: lighter reset (preserve userProfileSearchStatus for waitForTradingProfileReady); otherwise full reset.
  if (profileAlreadyPending) {
    yield put(setResubscribeableFieldsUndefined());
  } else {
    yield put(setResubscribeableFieldsUndefinedForAppReconnection());
  }
  yield put(resetStockData());
  yield put(resetReferencesPriceData());
  yield put(reloadSubscriptionBeUserSaga());
  if (!profileAlreadyPending) {
    yield put(getUserProfileReconnectSaga());
  }
  const isTradingProfileReady = yield call(waitForTradingProfileReady);
  if (!isTradingProfileReady) {
    if (document.visibilityState === 'visible') {
      yield delay(CONTEXT_REFRESH_RETRY_DELAY_MS);
      // Retry without profileAlreadyPending — onConnect request may have been lost (WS send failed before session ready).
      yield put(delegationContextRefreshRequest({ profileAlreadyPending: false }));
    }
    return;
  }

  const activeSession: DelegationSessionResponse | null = yield select(getActiveSession);
  if (activeSession) {
    yield put(fetchVehicleSearchHistorySubscriptionSaga());
    yield put(fetchBasketRequest());
    yield put(fetchSettingsRequest());
    yield put(resetEstimateHistory());
    yield put(fetchEstimateHistoryRequest());
    yield put(orderSubscriptionSaga());

    const currentEstimateId: string | undefined = yield select(getCurrentEstimateId);
    if (currentEstimateId) {
      yield put(fetchEstimateByIdRequest(currentEstimateId));
    }
  }
}

export function* watchWindowActivationForDelegationSyncSaga(): SagaIterator {
  const channel = yield call(createWindowActivationChannel);

  while (true) {
    yield take(channel);
    yield call(requestDelegationStateSyncSaga);
  }
}

export function* watchCrossTabDelegationSyncSaga(): SagaIterator {
  const channel = yield call(createCrossTabDelegationSyncChannel);

  while (true) {
    const syncEvent: { reason: DelegationSyncReason } = yield take(channel);
    if (syncEvent.reason === 'reset') {
      yield call(performFullDelegationReset, { requestUserProfile: true });
    } else {
      yield call(requestDelegationStateSyncSaga);
    }
  }
}

export function* watchDelegationContextRefreshQueueSaga(): SagaIterator {
  let activeRefreshTask: Task | null = null;

  while (true) {
    const action: ReturnType<typeof delegationContextRefreshRequest> = yield take(delegationContextRefreshRequest.type);

    if (activeRefreshTask) {
      yield cancel(activeRefreshTask);
    }

    activeRefreshTask = yield fork(delegationContextRefreshSaga, action);
  }
}

export function* DelegationSessionSagas(): SagaIterator {
  yield takeEvery(connectDelegationSessionRequest.type, sagaGuard(connectDelegationSessionRequestSaga));
  yield takeEvery(disconnectDelegationSessionRequest.type, sagaGuard(disconnectDelegationSessionRequestSaga));
  yield takeEvery(extendDelegationSessionRequest.type, sagaGuard(extendDelegationSessionRequestSaga));
  yield takeEvery(getActiveDelegationSessionRequest.type, sagaGuard(getActiveDelegationSessionRequestSaga));

  yield takeEvery(connectDelegationSessionResponse.type, sagaGuard(connectDelegationSessionResponseSaga));
  yield takeEvery(disconnectDelegationSessionResponse.type, sagaGuard(disconnectDelegationSessionResponseSaga));
  yield takeEvery(extendDelegationSessionResponse.type, sagaGuard(extendDelegationSessionResponseSaga));
  yield takeEvery(getActiveDelegationSessionResponse.type, sagaGuard(getActiveDelegationSessionResponseSaga));
  yield takeEvery(resetDelegationSessionSaga.type, sagaGuard(resetDelegationSessionRequestSaga));
  yield takeEvery(getPossibleSellersForDelegationSaga.type, sagaGuard(getPossibleSellersForDelegationRequestSaga));
  yield takeEvery(
    getPossibleSellersForDelegationResponseSaga.type,
    sagaGuard(getPossibleSellersForDelegationResponseHandlerSaga),
  );
  yield takeEvery(getPossibleBuyersForDelegationSaga.type, sagaGuard(getPossibleBuyersForDelegationRequestSaga));
  yield takeEvery(
    getPossibleBuyersForDelegationResponseSaga.type,
    sagaGuard(getPossibleBuyersForDelegationResponseHandlerSaga),
  );
  yield takeEvery(getDelegationSellerSaga.type, sagaGuard(getDelegationSellerRequestSaga));
  yield takeEvery(getDelegationSellerResponseSaga.type, sagaGuard(getDelegationSellerResponseHandlerSaga));
  yield takeEvery(cancelCurrentDelegationSellerSaga.type, sagaGuard(cancelCurrentDelegationSellerRequestSaga));

  yield takeEvery(delegationSessionUpdatedEvent.type, sagaGuard(delegationSessionUpdatedEventSaga));
  yield takeEvery(delegationSessionErrorAction.type, sagaGuard(delegationSessionErrorSaga));

  yield fork(watchDelegationContextRefreshQueueSaga);
  yield fork(watchWindowActivationForDelegationSyncSaga);
  yield fork(watchCrossTabDelegationSyncSaga);
  yield fork(watchHeartbeatLifecycleSaga);
}
